import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, TrendingDown, Waves } from 'lucide-react';
import { Area, AreaChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import PageShell from '../components/PageShell';
import { listNews } from '../api/news';
import ONBOARDING from '../data/onboarding';
import { guide } from '../components/guide/guideStore';
import { NEWS_LABELS, fmtDate, openNews } from './welcomeBits';
import { getFinanceHome, getSalesHome } from '../api/home';
import { formatNumber } from '../utils/format';
import { usePersistentValue } from '../hooks/usePersistentState';
import './WelcomePage.css';

/* ── ACCUEIL (refonte du 2026-10-01, sur le prototype de Codex) ───────────
   La page que TOUT LE MONDE a. Elle dit, dans l'ordre : de quelle entreprise
   on parle et à quelle date ses chiffres sont arrêtés ; trois cartes,
   la TRÉSORERIE (le solde réel, son trajet sur douze mois, ce que le plan de
   liquidité en fait d'ici à décembre), le RÉALISÉ à ce jour et l'ATTERRISSAGE
   de l'exercice ; ce qui appelle l'attention dans les prévisions ; enfin les
   trois dernières nouveautés de Palestre.

   SANS FINANCE (client Ventes & Ops seul, ou personne sans le périmètre
   financier), la mise en page reste la même (décision d'Olivier) : la
   trésorerie vient du plan de liquidité, la deuxième carte porte la
   facturation réalisée à ce jour et sa moyenne mensuelle, la troisième la
   facturation prévue et la sienne.

   Chaque appel part dans SON périmètre : refusé, sa carte ne s'affiche pas,
   et un vendeur ne verra jamais le compte de résultat en ouvrant l'Accueil.
   Le guide à reprendre a quitté la page avec le prototype : les guides ont
   leur section, dans le groupe Ressources. */

/* Va sur l'écran du guide, OUVRE le panneau Savoirs et rend la main au mode
   Guide (2026-09-16) : lancé depuis l'Accueil, on poursuit depuis la
   télécommande, sans avoir à aller chercher l'ampoule. */
export function playTopic(topic, { setPage, openPanel, step = 0 } = {}) {
  const key = (topic.steps.find(s => s.pageKey) || {}).pageKey || (topic.pages || [])[0];
  if (key && setPage) setPage(key);
  if (openPanel) openPanel();
  guide.start(topic.id);
  if (step > 0) guide.goto(step);
}

/** Les parcours ouverts à la personne : L'essentiel, puis ses modules. */
export function guidesFor(visibleModules) {
  return ONBOARDING.filter(gd => !gd.module || (visibleModules || []).includes(gd.module));
}

const num = (v) => (v == null || !Number.isFinite(Number(v)) ? null : Number(v));
/* Les montants de l'Accueil suivent la bascule CHF | kCHF de la bande de
   contexte (2026-10-03, kCHF par défaut), sans décimale. Le moins est
   typographique, comme sur les états. */
export const UNIT_LABEL = { kchf: 'kCHF', chf: 'CHF' };
export function kfmt(v, unit = 'kchf') {
  const n = num(v);
  if (n == null) return '—';
  const r = Math.round(unit === 'chf' ? n : n / 1000);
  return `${r < 0 ? '−' : ''}${formatNumber(Math.abs(r), 0)}`;
}
const pct = (v) => {
  const n = num(v);
  return n == null ? '' : `${n < 0 ? '−' : ''}${Math.abs(n).toFixed(1).replace('.', ',')} %`;
};
const neg = (v) => num(v) != null && num(v) < 0;

/* Les TENSIONS de l'Accueil, tirées des chiffres du serveur : la trésorerie qui
   passe sous zéro dans le plan de liquidité, puis une perte opérationnelle
   annuelle. Le texte se compose ici, les chiffres viennent du serveur.
   `h` traduit une clé de `cockpit:home`, `month` nomme un mois (minuscule en
   français, au fil de la phrase). */
export function buildTensions({ fin, year, h, month, inText, unit = 'kchf' }) {
  const out = [];
  const f = (v) => kfmt(v, unit);
  const u = UNIT_LABEL[unit];
  const plan = fin && fin.cash_plan;
  const low = plan && plan.low;
  if (low && low.value < 0) {
    const credit = num(plan.credit) || 0;
    const beyond = -low.value - credit;
    const first = plan.first_negative;
    const firstTxt = first ? h('attnFirstNeg', { m: inText(first) }) : '';
    let title;
    let text;
    if (credit > 0 && beyond > 0) {
      title = h('attnBeyondTitle', { v: f(beyond), u });
      text = h('attnBeyondText', { v: f(low.value), c: f(credit) });
    } else if (credit > 0) {
      title = h('attnCoveredTitle', { v: f(-low.value), u });
      text = h('attnCoveredText', { v: f(low.value), c: f(credit) });
    } else {
      title = h('attnNoCreditTitle', { v: f(-low.value), u });
      text = h('attnNoCreditText');
    }
    out.push({
      key: 'cash', icon: Waves,
      cat: h('attnCashCat', { m: month(low.month), y: year }),
      title, text: `${text} ${firstTxt}`.trim(),
      action: h('attnCashAction'), go: 'forecast-wc',
    });
  }
  if (fin && fin.has_finance && neg(fin.ebitda_landing)) {
    const ytd = num(fin.ebitda_ytd);
    const rest = num(fin.ebitda_rest);
    const from = (num(fin.closed_month) || 0) + 1;
    let text = '';
    if (ytd != null && rest != null && from <= 12) {
      const m = inText(from);
      if (rest >= 0) text = h('attnLossOffset', { r: f(rest), u, m, y: f(-ytd) });
      else if (ytd < 0) text = h('attnLossAdds', { r: f(-rest), u, m, y: f(-ytd) });
      else text = h('attnLossTurns', { r: f(-rest), u, m, y: f(ytd) });
    }
    out.push({
      key: 'result', icon: TrendingDown,
      cat: h('attnResultCat', { y: year }),
      title: h('attnLossTitle', { v: f(-fin.ebitda_landing), u }),
      text,
      action: h('attnResultAction'), go: 'forecast-out',
    });
  }
  return out;
}

/* Une valeur d'une carte : son libellé, le chiffre, la ligne qui le situe. */
function Figure({ label, value, note, alert = false }) {
  return (
    <div className="wch-fig">
      <p className="wch-fig__l">{label}</p>
      <div className={`wch-fig__v${alert ? ' is-alert' : ''}`}>{value}</div>
      {note && <span className="wch-fig__n">{note}</span>}
    </div>
  );
}

function CardLink({ label, onClick }) {
  return (
    <button type="button" className="wch-link" onClick={onClick}>
      <span>{label}</span><ArrowRight size={14} strokeWidth={1.8} aria-hidden="true" />
    </button>
  );
}

export default function WelcomePage({ visibleModules = [], setPage, onGoCockpitTab, activeTenant = null, year = null }) {
  const { t, i18n } = useTranslation();
  const h = (k, o) => t(`cockpit:home.${k}`, o);
  const [news, setNews] = useState([]);

  useEffect(() => {
    listNews(null, 3).then(r => setNews(r.data || [])).catch(() => setNews([]));
  }, []);

  const tid = activeTenant?.id || null;
  const yr = year || new Date().getFullYear();
  const hasSales = (visibleModules || []).includes('scenarii');
  const [unit, setUnit] = usePersistentValue(`home_unit_${tid}`, 'kchf');
  const pageUnit = useMemo(() => ({ value: unit, onChange: setUnit }), [unit, setUnit]);
  const k = (v) => kfmt(v, unit);
  const uLabel = UNIT_LABEL[unit] || 'kCHF';
  const [fin, setFin] = useState(null);
  const [sal, setSal] = useState(null);
  useEffect(() => {
    if (!tid) return undefined;
    let alive = true;
    setFin(null); setSal(null);
    getFinanceHome(tid, yr).then((r) => { if (alive) setFin((r && r.data) || false); })
      .catch(() => { if (alive) setFin(false); });
    /* VENTES : la facturation suit le MODULE, pas seulement le périmètre. */
    if (hasSales) {
      getSalesHome(tid, yr).then((r) => { if (alive) setSal((r && r.data) || false); })
        .catch(() => { if (alive) setSal(false); });
    } else setSal(false);
    return () => { alive = false; };
  }, [tid, yr, hasSales]);

  const month = (m) => t(`common:monthsFull.${m}`);
  /* Au fil d'une phrase, un mois s'écrit en minuscule en français. */
  const lower = (m) => (String(i18n.language || 'fr').startsWith('fr') ? month(m).toLowerCase() : month(m));
  const closed = fin ? num(fin.closed_month) || 0 : 0;
  const finMode = !!(fin && fin.has_finance);

  /* L'en-tête : la date d'arrêt de la comptabilité et celle de la dernière
     saisie de planification. */
  const lcp = activeTenant?.last_closed_period || null;
  const lcpDate = lcp ? new Date(`${String(lcp).slice(0, 10)}T00:00:00`) : null;
  const fresh = [
    finMode && lcpDate ? h('freshBooks', { m: lower(lcpDate.getMonth() + 1), y: lcpDate.getFullYear() }) : null,
    fin && fin.forecast_updated_at ? h('freshPlan', { d: fmtDate(fin.forecast_updated_at, true) }) : null,
  ].filter(Boolean).join(' · ');

  /* ── Carte 1 : la trésorerie ─────────────────────────────────────────── */
  const plan = fin && fin.cash_plan;
  const history = useMemo(
    () => ((fin && fin.cash_history) || []).map(x => ({ ...x, k: x.value == null ? null : (unit === 'chf' ? x.value : x.value / 1000) })),
    [fin, unit],
  );
  const hasHistory = history.some(h => h.value != null);
  const lastPt = [...history].reverse().find(h => h.value != null) || null;
  const realCash = finMode && closed > 0 ? num(fin.cash) : null;
  const cashNow = realCash != null ? realCash : (plan ? num(plan.plan_opening) : null);
  const showCash = !!(fin && (cashNow != null || (plan && plan.low)));
  const lastDay = (y, m) => new Date(y, m, 0).getDate();
  const shortMonth = (x) => `${t(`common:months.${x.month}`)} ${String(x.year).slice(2)}`;

  /* ── Cartes 2 et 3 ───────────────────────────────────────────────────── */
  const period = (a, b) => (a === b ? h('periodOne', { a: month(a), y: yr }) : h('periodRange', { a: month(a), b: lower(b), y: yr }));
  const cockpitOr = (tab, fallback) => () => {
    if (onGoCockpitTab) onGoCockpitTab(tab); else if (setPage) setPage(fallback);
  };
  const billingDone = sal && num(sal.billing_done);
  const billingThrough = sal && num(sal.billing_done_through);
  const billingPlan = sal && num(sal.billing_plan);
  const billingFrom = sal && num(sal.billing_plan_from);
  const billingMonths = sal && num(sal.billing_plan_months);

  const tensions = buildTensions({ fin: fin || null, year: yr, h, month, inText: lower, unit });

  return (
    <PageShell
      title={activeTenant?.name || ''}
      tools={fresh ? <p className="wch-fresh">{fresh}</p> : null}
      unit={pageUnit}
    >
      <div className="wch">
        <div className="wch-cards">
          {showCash && (
            <section className="wch-card wch-card--cash" aria-label={h('cashTitle')}>
              <h2 className="wch-card__t">{h('cashTitle')}</h2>
              <div className="wch-cash">
                <span className={`wch-cash__v${neg(cashNow) ? ' is-alert' : ''}`}>{k(cashNow)}</span>
                <span className="wch-cash__u">{uLabel}</span>
                <span className="wch-cash__d">
                  {realCash != null
                    ? h('cashBank', { d: lastDay(yr, closed), m: lower(closed), y: yr })
                    : h('cashStart')}
                </span>
              </div>
              {hasHistory && (
                <div className="wch-hist">
                  <div className="wch-hist__h">
                    <span>{h('cashHistory')}</span>
                    <span className="wch-muted">{`${month(history[0].month)} ${history[0].year} → ${lower(history[history.length - 1].month)} ${history[history.length - 1].year}`}</span>
                  </div>
                  <div className="wch-hist__chart">
                    <ResponsiveContainer width="100%" height={150}>
                      <AreaChart data={history} margin={{ top: 14, right: 36, bottom: 0, left: 8 }}>
                        {/* Le premier mois s'aligne à gauche, le dernier à droite :
                            centrés sur leur point, ils sortaient du cadre. */}
                        <XAxis dataKey="month" tickLine={false} axisLine={{ stroke: 'var(--color-border)' }}
                               interval={0}
                               tick={({ x, y, index }) => {
                                 const pt = history[index];
                                 const last = index === history.length - 1;
                                 if (!pt || !(index === 0 || last || pt.month === 1)) return <g />;
                                 return (
                                   <text x={x} y={y + 12} fontSize={11} fill="var(--color-text-muted)"
                                         textAnchor={index === 0 ? 'start' : (last ? 'end' : 'middle')}>
                                     {shortMonth(pt)}
                                   </text>
                                 );
                               }} />
                        <YAxis hide domain={[(min) => Math.min(0, min), 'auto']} />
                        <Area type="linear" dataKey="k" connectNulls={false} isAnimationActive={false}
                              stroke="var(--color-accent)" strokeWidth={2.2}
                              fill="var(--color-accent-tint-strong)" fillOpacity={1}
                              dot={(props) => (props.index === history.indexOf(lastPt)
                                ? <circle key="last" cx={props.cx} cy={props.cy} r={4} fill="var(--color-accent)" />
                                : <g key={props.index} />)}
                              label={(props) => (props.index === history.indexOf(lastPt)
                                ? <text key="lbl" x={props.x - 6} y={props.y + 22} textAnchor="end"
                                        fontSize={13} fontWeight={600} fill="var(--color-text)">{k(lastPt.value)}</text>
                                : null)} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
              {plan && plan.low && (
                <div className="wch-outlook">
                  <Figure label={h('cashLow')} value={k(plan.low.value)} alert={neg(plan.low.value)}
                          note={h('cashLowIn', { m: lower(plan.low.month) })} />
                  {plan.eoy != null && (
                    <Figure label={h('cashEoy')} value={k(plan.eoy)} alert={neg(plan.eoy)} />
                  )}
                </div>
              )}
              <CardLink label={h('cashOpen')} onClick={() => setPage && setPage('forecast-wc')} />
            </section>
          )}

          {finMode && (
            <>
              <section className="wch-card">
                <div className="wch-card__head">
                  <h2 className="wch-card__t">{h('doneTitle')}</h2>
                  {closed > 0 && <p className="wch-card__p">{period(1, closed)}</p>}
                </div>
                <Figure label={h('revenue')} value={k(fin.revenue_ytd)}
                        note={fin.revenue_var_pct == null ? null
                          : h('vsPrev', { v: `${num(fin.revenue_var_pct) >= 0 ? '+' : ''}${pct(fin.revenue_var_pct)}`, y: yr - 1 })} />
                <Figure label={h('result')} value={k(fin.ebitda_ytd)} alert={neg(fin.ebitda_ytd)}
                        note={fin.ebitda_ytd_pct == null ? h('beforeDep')
                          : `${h('beforeDep')} · ${h('margin', { v: pct(fin.ebitda_ytd_pct) })}`} />
                <p className="wch-unit">{uLabel}</p>
                <CardLink label={h('doneOpen')} onClick={cockpitOr('rent', 'financials-synthesis')} />
              </section>
              <section className="wch-card">
                <div className="wch-card__head">
                  <h2 className="wch-card__t">{h('landingTitle')}</h2>
                  <p className="wch-card__p">{h('landingPeriod', { y: yr })}</p>
                </div>
                <Figure label={h('revenue')} value={k(fin.revenue_landing)}
                        note={h('landingRevNote')} />
                <Figure label={h('result')} value={k(fin.ebitda_landing)} alert={neg(fin.ebitda_landing)}
                        note={fin.ebitda_landing_pct == null ? h('beforeDep')
                          : `${h('beforeDep')} · ${h('margin', { v: pct(fin.ebitda_landing_pct) })}`} />
                <p className="wch-unit">{uLabel}</p>
                <CardLink label={h('landingOpen')} onClick={() => setPage && setPage('forecast-out')} />
              </section>
            </>
          )}

          {!finMode && sal && (
            <>
              <section className="wch-card">
                <div className="wch-card__head">
                  <h2 className="wch-card__t">{h('billingDoneTitle')}</h2>
                  {billingThrough ? <p className="wch-card__p">{period(1, billingThrough)}</p> : null}
                </div>
                <Figure label={h('billingLabel')} value={k(billingDone)} />
                <Figure label={h('billingAvg')}
                        value={billingDone != null && billingThrough ? k(billingDone / billingThrough) : '—'} />
                <p className="wch-unit">{uLabel}</p>
                <CardLink label={h('billingDoneOpen')} onClick={() => setPage && setPage('sales-billing')} />
              </section>
              <section className="wch-card">
                <div className="wch-card__head">
                  <h2 className="wch-card__t">{h('billingPlanTitle')}</h2>
                  {billingFrom ? <p className="wch-card__p">{period(billingFrom, 12)}</p> : null}
                </div>
                <Figure label={h('billingPlanLabel')} value={k(billingPlan)} />
                <Figure label={h('billingAvg')}
                        value={billingPlan != null && billingMonths ? k(billingPlan / billingMonths) : '—'} />
                <p className="wch-unit">{uLabel}</p>
                <CardLink label={h('billingPlanOpen')} onClick={() => setPage && setPage('sales-backlog')} />
              </section>
            </>
          )}
        </div>

        {tensions.length > 0 && (
          <section className="wch-sec">
            <div className="wch-sec__head">
              <h2>{h('attnTitle')}</h2>
              <span className="wch-count">
                {tensions.length > 1
                  ? h('attnCountMany', { n: tensions.length })
                  : h('attnCountOne')}
              </span>
            </div>
            <div className="wch-attn">
              {tensions.map(tn => {
                const Icon = tn.icon;
                return (
                  <div className="wch-attn__row" key={tn.key}>
                    <span className="wch-attn__mark"><Icon size={15} strokeWidth={1.8} aria-hidden="true" /></span>
                    <div className="wch-attn__copy">
                      <p className="wch-attn__cat">{tn.cat}</p>
                      <strong>{tn.title}</strong>
                      {tn.text && <p>{tn.text}</p>}
                    </div>
                    <button type="button" className="wch-btn" onClick={() => setPage && setPage(tn.go)}>
                      {tn.action} <ArrowRight size={13} strokeWidth={1.8} aria-hidden="true" />
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {news.length > 0 && (
          <section className="wch-sec" aria-label={h('newsTitle')}>
            <div className="wch-sec__head">
              <h2>{h('newsTitle')}</h2>
              <span className="wch-count">{news.length > 1 ? h('newsCountMany', { n: news.length }) : h('newsCountOne')}</span>
            </div>
            <div className="wch-news">
              {news.map(n => (
                <button type="button" key={n.id} className="wch-news__item"
                        onClick={() => openNews(n.id, setPage)}>
                  <span className="wch-news__meta">{fmtDate(n.news_date, true, true)} · {NEWS_LABELS[n.module] || n.module}</span>
                  <strong>{n.text}</strong>
                  {n.intro && <span className="wch-news__copy">{n.intro}</span>}
                  <span className="wch-news__go">{h('newsDiscover')} <ArrowRight size={13} strokeWidth={1.8} aria-hidden="true" /></span>
                </button>
              ))}
            </div>
          </section>
        )}
      </div>
    </PageShell>
  );
}
